import React, { useMemo } from 'react';
import { MedicalDataTypes } from '../../../../../primary-record-types';
import { RangeRectangle, getGraphData } from '../../../../../utils/medicalDataUtils';
import {
    VictoryAxis,
    VictoryChart,
    VictoryLine,
    VictoryScatter,
    VictoryTheme,
    VictoryTooltip,
    VictoryVoronoiContainer, // Import VictoryVoronoiContainer
} from 'victory-native';
import getDateString from '../../../../../utils/getDateString';
import { Rect } from 'react-native-svg';
import SizedView from '../../../../v2BaseComponents/SizedView';
import { colors } from '../../../../../utils/theme';

export type HistoryChartOwnProps = {
    labs: MedicalDataTypes.PrimaryRecordLab[];
    code: string;
};

export type HistoryChartProps = HistoryChartOwnProps;

export const hasSufficientData = (labs: MedicalDataTypes.PrimaryRecordLab[], code: string): boolean => {
    const { data } = getGraphData(labs, code);
    const uniqueDates = new Set(data.map((_) => _.x.toISOString()));
    return uniqueDates.size > 1;
};

type CustomReferenceRangeProps = {
    data: RangeRectangle[];
};

const ReferenceRange: React.FC<CustomReferenceRangeProps & any> = (props) => {
    const getCoordinates = (rectangle: RangeRectangle) => {
        const x1 = props.scale.x(rectangle.x1);
        const x2 = props.scale.x(rectangle.x2);
        const y1 = props.scale.y(rectangle.y1);
        const y2 = props.scale.y(rectangle.y2);
        const width = x2 - x1;
        const height = y1 - y2;
        return { x: x1, y: y2, width, height };
    };

    return (
        <>
            {props.data.map((rectangle: RangeRectangle, index: number) => {
                return (
                    <Rect
                        key={index}
                        {...getCoordinates(rectangle)}
                        fill={colors.Success}
                        opacity={0.2}
                        testID="reference-range-rectangle"
                    />
                );
            })}
        </>
    );
};

const HistoryChart: React.FC<HistoryChartProps> = ({ labs, code }) => {
    const { data, referenceRange, rangeRectangles, inRangeData, outOfRangeData, unit } = useMemo(
        () => getGraphData(labs, code),
        [labs, code],
    );

    const getTooltipLabel = (tooltipInput: { datum: { x: Date; y: number } }) => {
        return `${tooltipInput.datum.y}${unit ? ` ${unit}` : ''}\n${getDateString(
            tooltipInput.datum.x.getTime(),
            'date-time',
        )}`;
    };

    if (!hasSufficientData(labs, code)) return null;

    return (
        <SizedView style={{ width: '100%', height: 350 }}>
            {({ width }) => (
                <VictoryChart
                    scale={{
                        x: 'time',
                    }}
                    width={width}
                    height={350}
                    theme={VictoryTheme.material}
                    containerComponent={<VictoryVoronoiContainer />} // Use VictoryVoronoiContainer
                >
                    <ReferenceRange data={rangeRectangles as any} />
                    <VictoryAxis
                        fixLabelOverlap
                        tickFormat={(tick) => {
                            return getDateString((tick as Date).getTime(), 'month-year');
                        }}
                        style={{
                            grid: { stroke: colors['Primary'], opacity: 0.2 },
                            axis: {
                                stroke: colors['Dark-Text'],
                                strokeWidth: 3,
                            },
                        }}
                        tickCount={8}
                    />
                    <VictoryAxis
                        dependentAxis
                        style={{
                            grid: { stroke: colors['Primary'], opacity: 0.2 },
                            axis: {
                                stroke: colors['Dark-Text'],
                                strokeWidth: 3,
                            },
                        }}
                        domain={[referenceRange.min, referenceRange.max]}
                    />
                    <VictoryLine
                        data={data}
                        style={{
                            data: {
                                stroke: colors['Dark-Text'],
                                strokeWidth: 3,
                            },
                        }}
                    />
                    <VictoryScatter
                        data={outOfRangeData}
                        style={{
                            data: {
                                fill: colors['Primary-Reg'],
                            },
                        }}
                        size={5}
                        labelComponent={<VictoryTooltip />}
                        labels={getTooltipLabel}
                    />
                    <VictoryScatter
                        data={inRangeData}
                        style={{
                            data: {
                                fill: colors.Success,
                            },
                        }}
                        size={5}
                        labelComponent={<VictoryTooltip />}
                        labels={getTooltipLabel}
                    />
                </VictoryChart>
            )}
        </SizedView>
    );
};

export default React.memo(HistoryChart);
